import * as React from 'react';
import { getLocalCache, setLocalCache } from '../common/AppUtils';
import Paper from '@mui/material/Paper';
import {
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  TablePagination,
  IconButton,
} from '@mui/material';
import { Box, Button, Typography, Tooltip } from '@mui/material';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';

const NO_DATA_AVAILABLE_STRING =
  'No data available, please start adding now !!!';
const LOCAL_STORAGE_KEY = 'rowsPerPagePreference';
const PAGINATION_OPTIONS = [10, 15, 20, 100];
const CELL_BORDER = '1px solid #ddd';
const ROW_HEIGHT = 50;

const COLOR_APPROVED = "#28B463";

export default function StaffSubstituteTable({ columns, rows, relatedData, onEdit, onApproveToggle, showPagination=true }) {
  const [orderBy, setOrderBy] = React.useState(null);
  const [order, setOrder] = React.useState('asc');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(
    getLocalCache(LOCAL_STORAGE_KEY, PAGINATION_OPTIONS[1]),
  );

  // Sorting function
  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10); // 2nd arg is the numerical base system - Should be always 10
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setLocalCache(LOCAL_STORAGE_KEY, newRowsPerPage);
  };

  const sortedRows = React.useMemo(() => {
    if (!orderBy) return rows;

    return [...rows].sort((a, b) => {
      if (a[orderBy] < b[orderBy]) return order === 'asc' ? -1 : 1;
      if (a[orderBy] > b[orderBy]) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, orderBy, order]);

  const visibleRows = React.useMemo(
    () =>
      showPagination
        ? sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : sortedRows,
    [sortedRows, page, rowsPerPage, showPagination],
  );

  return (
    <React.Fragment>
      {rows.length === 0 ? (
        // Display message when there are no rows
        <Box
          sx={{
            textAlign: 'left',
          }}
        >
          <Typography variant="h5">{NO_DATA_AVAILABLE_STRING}</Typography>
        </Box>
      ) : (
        <React.Fragment>
          <TableContainer
            component={Paper}
            sx={{
              border: CELL_BORDER,
              borderRadius: '8px',
              overflow: 'show',
              maxWidth: 'lg'
            }}
          >
            <Table sx={{ borderCollapse: 'collapse' }}>
              <TableHead
                style={{
                  backgroundColor: '#f0f0f0',
                  color: '#333',
                  // borderBottom: '2px solid #ccc',
                }}
              >
                <TableRow
                  sx={{
                    display: 'flex',
                  }}
                >
                  {/* <TableCell
                    key='Visible'
                    align='center'
                    sx={{
                      flex: '0 0 56px',
                      maxWidth: '56px',
                      minWidth: '56px',
                      borderRight: 'none',
                    }}
                  >
                    <Tooltip title={'Visibility to staff'}>
                      <Typography variant="h5" margin={0}>
                        Visible
                      </Typography>
                    </Tooltip>
                  </TableCell> */}
                  {columns.map((column, col_index) => (
                    <TableCell
                      key={column.attr}
                      align={column.align || 'center'}
                      sx={{
                        flex: column.attr === 'edit' ? '0 0 48px' : 1,
                        maxWidth: column.attr === 'edit' ? '48px' : 'none',
                        minWidth: column.attr === 'edit' ? '48px' : '72px',
                        wordBreak: 'break-word', // Break long words at unnatural point when necessary
                        display: '-webkit-box', // Use webkit box model
                        WebkitBoxOrient: 'vertical', // Set the orientation to vertical for multi-line text
                        WebkitLineClamp: 2, // Limit the text to 2 lines
                        overflow: 'auto', // Add scroll for overflow lines
                        textOverflow: 'ellipsis', // Show ellipsis (...) for overflowing text
                        // borderRight: CELL_BORDER,
                      }}
                    >
                      {column.attr === 'edit' ? (
                        <Typography variant="h5">{column.label}</Typography>
                      ) : (
                        // <TableSortLabel
                        //   active={orderBy === column.attr}
                        //   direction={orderBy === column.attr ? order : 'asc'}
                        //   onClick={() => handleSort(column.attr)}
                        // >
                          <Tooltip title={column.help_text || ''}>
                            <Typography variant="h5" margin={0}>
                              {column.label}
                            </Typography>
                          </Tooltip>
                        // </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row, row_index) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      minHeight: `${ROW_HEIGHT}px`,
                      display: 'flex',
                      backgroundColor: row_index % 2 === 0 ? '#fff' : '#fafafa', // Alternating row colors
                      '&:hover': {
                        backgroundColor: '#f0f0f0', // Hover effect to highlight row
                      },
                    }}
                  >
                    {/* <TableCell
                      align='center'
                      sx={{
                        flex: '0 0 56px',
                        maxWidth: '56px',
                        minWidth: '56px',
                        borderBottom: '1px solid #eee', // Subtle border for table rows
                        borderRight: 'none'
                      }}
                    >
                      <Button
                        size="small"
                        onClick={() => onApproveToggle(row, row['status'] === 'in_review' ? 'approved' : 'in_review')}
                        sx={{
                          width: 22,
                          height: 22,
                          // padding: 0.25,       // tighter spacing
                        }}
                      >
                        {row['status'] === 'auto_approved' || row['status'] === 'approved' ? (
                          <Tooltip title={'Visible'}>
                            <CheckCircleOutlinedIcon
                              sx={{
                                fontSize: 16,
                                color: COLOR_APPROVED
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title={'Not yet visible'}>
                            <AccessTimeOutlinedIcon
                              sx={{
                                fontSize: 16,
                                // color: COLOR_AWAITING
                              }}
                            />
                          </Tooltip>
                        )}
                      </Button>

                    </TableCell> */}
                    {columns.map((column, col_index) => (
                      <TableCell
                        align={column.align || 'center'}
                        sx={{
                          flex: column.attr === 'edit' ? '0 0 48px' : 1,
                          maxWidth: column.attr === 'edit' ? '48px' : 'none',
                          minWidth: column.attr === 'edit' ? '48px' : '72px',
                          borderBottom: '1px solid #eee', // Subtle border for table rows
                          color: '#333', // Lighter text color for table content
                          wordBreak: 'break-word', // Break long words at unnatural point when necessary
                          display: '-webkit-box', // Use webkit box model
                          WebkitBoxOrient: 'vertical', // Set the orientation to vertical for multi-line text
                          WebkitLineClamp: 3, // Limit the text to 2 lines
                          overflow: 'auto', // Add scroll for overflow lines
                          textOverflow: 'ellipsis', // Show ellipsis (...) for overflowing text
                          // borderRight: '1px solid #eee',
                        }}
                      >
                        {column.attr === 'edit' ? (
                          // ToDo: Should use row.id instead of index ?
                          <Button
                            color="secondary"
                            onClick={() => onEdit(row)}
                            fontSize="small"
                            sx={{
                              height: 24, // Reduce button height
                              width: 24, // Explicit button width
                              minWidth: 32, // Override MUI's default min-width of 56px
                              padding: 0,
                            }}
                          >
                            <EditTwoToneIcon
                              fontSize="small"
                              sx={{
                                fontSize: 20,
                              }}
                            />
                          </Button>
                        ) : column.related &&
                          relatedData[column.attr][row[column.attr]] ? (
                          <Typography variant="body2">
                            {relatedData[column.attr][row[column.attr]].label}
                          </Typography>
                        ) : (
                          <Typography variant="body2">
                            {row[column.attr]}
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {showPagination && (
            <Box display="flex" justifyContent="center">
              <TablePagination
                rowsPerPageOptions={PAGINATION_OPTIONS}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          )}
          
        </React.Fragment>
      )}
    </React.Fragment>
  );
}
